import React from 'react';
import { FiMail, FiCheckSquare, FiUsers } from 'react-icons/fi';
import { TenantStatusBadge } from './TenantStatus';

const PlanStatusBar = ({ 
    tenant, 
    className = "",
    showUpgradeButton = false,
    onUpgrade,
    compact = false 
}) => {
    if (!tenant) return null;

    // Helpers pour l'affichage de l'usage du plan
    const getUsagePercent = (used, max) => {
        if (!max || max === 0) return 0;
        return Math.min(100, Math.round((used / max) * 100));
    };

    const getUsageColorClass = (percent) => {
        // green (<50), yellow (>=50), orange (>=75), red (>=90), full (100)
        if (percent >= 100) return 'bg-red-600';
        if (percent >= 90) return 'bg-red-500';
        if (percent >= 75) return 'bg-orange-500';
        if (percent >= 50) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    const formatLimit = (limit) => {
        return limit === 999999 ? '∞' : limit.toLocaleString();
    };

    const resources = [
        {
            icon: FiMail,
            label: 'Messages',
            used: tenant?.usage?.messages || 0,
            max: tenant?.limits?.maxMessages || 0
        },
        {
            icon: FiCheckSquare,
            label: 'Todos',
            used: tenant?.usage?.todos || 0,
            max: tenant?.limits?.maxTodos || 0
        },
        {
            icon: FiUsers,
            label: tenant?.plan === 'free' ? 'Utilisateur (limite: 1)' : 'Utilisateurs',
            used: tenant?.usage?.users || 0,
            max: tenant?.limits?.maxUsers || 0
        }
    ];

    const hasWarnings = resources.some(r => getUsagePercent(r.used, r.max) >= 90);

    return (
        <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 ${className}`}>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <h3 className="text-md font-medium text-gray-900 dark:text-white">
                        Plan {tenant?.plan?.charAt(0).toUpperCase() + tenant?.plan?.slice(1)}
                    </h3>
                    <TenantStatusBadge tenant={tenant} />
                </div>
                {showUpgradeButton && onUpgrade && (
                    <button
                        onClick={onUpgrade}
                        className="text-sm font-bold bg-primary text-white dark:text-black px-3 py-1 rounded hover:bg-primary-hover transition"
                        disabled={typeof tenant.canUpgrade === 'boolean' ? !tenant.canUpgrade : false}
                    >
                        Modifier
                    </button>
                )}
            </div>

            <div className={`${compact ? 'space-y-2' : 'space-y-3'}`}>
                {resources.map((resource, index) => {
                    const percent = getUsagePercent(resource.used, resource.max);
                    const colorClass = getUsageColorClass(percent);
                    const Icon = resource.icon;
                    
                    return (
                        <div key={index}>
                            <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                    <Icon className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} text-gray-500 dark:text-gray-300`} aria-hidden="true" />
                                    <span className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600 dark:text-gray-400`}>
                                        {resource.label}
                                    </span>
                                </div>
                                <span className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600 dark:text-gray-400`}>
                                    {resource.used} / {formatLimit(resource.max)} ({percent}%)
                                </span>
                            </div>
                            <div className={`w-full bg-gray-200 dark:bg-gray-700 rounded-full ${compact ? 'h-1.5' : 'h-2'} ${percent >= 100 ? 'ring-2 ring-red-400/30' : ''}`}>
                                <div 
                                    className={`${colorClass} ${compact ? 'h-1.5' : 'h-2'} rounded-full transition-all duration-300`}
                                    style={{ width: `${percent}%` }}
                                />
                            </div>
                            {percent >= 90 && (
                                <p className={`${compact ? 'text-xs' : 'text-xs'} text-orange-600 dark:text-orange-400 mt-4`}>
                                    ⚠️ Limite bientôt atteinte
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>

            {hasWarnings && tenant?.plan === 'free' && (
                <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-md">
                    <p className="text-xs text-orange-800 dark:text-orange-200">
                        Mettez à niveau votre plan pour plus de ressources !
                    </p>
                </div>
            )}
        </div>
    );
};

export default PlanStatusBar;